import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AlertTriangle, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import { API_MB_PLATFORM_HOST } from "../../../../constants/common";

import type { BdcResendMethod, BdcStartRequest } from "../../../../api/bdc.api";
import {
  isDeterministicBdcError,
  mapBdcAxiosError,
  type BdcRecoverableField,
} from "../../../../exceptions/creditReportExceptions";
import { useBdcConfirmPin } from "../../../../hooks/vault/mutations/useBdcConfirmPin";
import { useBdcResendPin } from "../../../../hooks/vault/mutations/useBdcResendPin";
import { useBdcStart } from "../../../../hooks/vault/mutations/useBdcStart";
import { useInvalidateVaultFolder } from "../../../../hooks/vault/queries/useVaultFolder";
import { useBdcStatus } from "../../../../hooks/vault/queries/useBdcStatus";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  Folder,
  Requirement,
  Status,
} from "../../../../redux/interfaces/folder.types";
import {
  setActiveRequirement,
  setActiveVaultTab,
} from "../../../../redux/slices/vaultSlice";
import {
  isBdcActorMismatch,
  isNativeBdcSupported,
  resolveBdcExpectedActor,
} from "../../../../utils/vault/bdcActor";
import { Button } from "../../../ui/button";
import { useScopedVaultRequirementId } from "../../ui/VaultRequirementScopeContext";
import {
  BDC_COMPANY_DATA_SLUG,
  createBdcCompanyDataRequirement,
  hasValidatedPersonForm,
  isBdcReportReplacementPending,
  isLegacyBdcCaptureRecoveryEligible,
  isBdcAuthorizationRequirement,
  isBdcCompanyDataRequirement,
  isIdentityRequirement,
  isProofOfAddressRequirement,
} from "../../utils/requirementGrouping";
import { BdcConsents } from "./BdcConsents";
import { BdcPinConfirm } from "./BdcPinConfirm";
import { BdcStatusCards } from "./BdcStatusCards";
import {
  requiresIdentityReviewForBdcFields,
  type DataValidationNavigationOptions,
} from "../../utils/bdcRecovery";

/**
 * BdcAuthorizationNative — orquestador del paso NATIVO de Buro de Credito.
 *
 * El usuario otorga consentimientos y confirma el NIP; los datos de
 * identidad/domicilio los arma el backend (server-side).
 *
 * Estados del paso:
 *  - Al montar: GET bdc/status para recuperar el estado actual
 *    (read-only, NO dispara el NIP).
 *  - error HTTP transitorio (502/timeout en bdc/status) -> conserva el resultado
 *    terminal guardado; sin él, muestra ERROR REINTENTABLE.
 *  - status completed -> tarjeta completado (+ refresca el folder).
 *  - status invalid / is_active=false -> tarjeta de reporte invalidado.
 *  - status pin_validation -> RESUME directo a la captura del NIP (no re-dispara).
 *  - status capturing_inputs -> INTRO (consentimientos).
 *
 * INVARIANTE CRITICA: el NIP solo se dispara con `POST bdc/start` en el click
 * explicito de "Continuar" despues de aceptar consentimientos. NUNCA en un
 * useEffect de montaje.
 */

type Phase =
  | "loading"
  | "intro"
  | "pin"
  | "completed"
  | "in_review"
  | "long_report"
  | "recovering"
  | "read_only"
  | "read_only_pin"
  | "participant_read_only"
  | "legacy"
  | "invalid"
  | "error";

const DEFAULT_RETRY_PIN = 10;
const DEFAULT_RESEND_PIN = 5;
const WRONG_PIN_CODE = "1009";
/** Code del BFF: consentimiento faltante antes de disparar el NIP. */
const CONSENT_MISSING_CODE = "BDC040";
/** Codigos upstream que significan "el reporte ya esta completado". */
const ALREADY_COMPLETED_CODES = new Set(["1000", "1001", "1012"]);
const MISSING_RECOVERABLE_TARGET_DESCRIPTION =
  "Este expediente no tiene el paso necesario para corregir estos campos. " +
  "Contacta a soporte para actualizar el folio.";

interface Props {
  folder: Folder;
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
  onSetNavigationHidden?: (hidden: boolean) => void;
  /**
   * Internal `/folders` mount. Staff can inspect the requirement but must not
   * call the actor-only BDC endpoints or attempt to start/confirm a NIP.
   */
  isAdminContext?: boolean;
  onNavigateToRequirement?: (
    requirement: Requirement,
    subfolderId: string,
    options?: { forceBdcCompanyDataEdit?: boolean },
  ) => void;
  onNavigateToDataValidation?: (
    options?: DataValidationNavigationOptions,
  ) => boolean;
}

export const BdcAuthorizationNative: React.FC<Props> = ({
  folder,
  onRegisterSubmit,
  onSetNavigationHidden,
  isAdminContext,
  onNavigateToRequirement,
  onNavigateToDataValidation,
}) => {
  const dispatch = useAppDispatch();
  const activeRequirement = useAppSelector(
    (state) => state.vault.activeRequirement,
  );
  const activeTab = useAppSelector((state) => state.vault.activeTab);
  const scopedRequirementId = useScopedVaultRequirementId();
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);

  // Native BDC mutations accept only a vault actor JWT; staff can read status.
  // Keep a pathname fallback for legacy documentation-map call sites, while
  // allowing explicit callers (FormView) to be authoritative.
  const isReadOnlyContext = useMemo(() => {
    if (isAdminContext !== undefined) return isAdminContext;
    if (typeof window === "undefined") return false;
    return !window.location.pathname.includes("/vault/");
  }, [isAdminContext]);

  const bdcRequirement = useMemo(
    () =>
      resolveBdcRequirement(
        folder,
        activeTab,
        activeRequirement,
        scopedRequirementId,
      ),
    [folder, activeTab, activeRequirement, scopedRequirementId],
  );

  const requirementId = bdcRequirement?.id;
  const reqStatus = bdcRequirement?.status;
  const bdcSubfolder = useMemo(
    () => resolveRequirementSubfolder(folder, bdcRequirement),
    [bdcRequirement, folder],
  );
  const expectedActor = resolveBdcExpectedActor(bdcSubfolder?.slug);
  const isParticipantMismatch =
    !isReadOnlyContext && isBdcActorMismatch(jwtToken, bdcSubfolder?.slug);
  const nativeBdcSupported =
    !bdcRequirement || isNativeBdcSupported(bdcRequirement, bdcSubfolder);

  // Read terminal requirements too, so the card reflects the current SIC state.
  const statusQuery = useBdcStatus(requirementId, {
    enabled:
      Boolean(requirementId) && !isParticipantMismatch && nativeBdcSupported,
    isAdminContext: isReadOnlyContext,
  });
  const status = nativeBdcSupported ? statusQuery.data : undefined;
  const usesNativeBdc =
    nativeBdcSupported && status?.sic_source !== "circulo_de_credito";
  const refetchStatus = statusQuery.refetch;

  // Mutations.
  const startMutation = useBdcStart(folder.id);
  const confirmMutation = useBdcConfirmPin(folder.id);
  const resendMutation = useBdcResendPin(folder.id);
  const invalidateFolder = useInvalidateVaultFolder(folder.id);

  const [acceptTyc, setAcceptTyc] = useState(false);
  const [acceptElectronicMedia, setAcceptElectronicMedia] = useState(false);
  const [pinEntryResetKey, setPinEntryResetKey] = useState(0);

  // Anti double-fire: once start has successfully advanced to pin_validation we
  // never re-fire it. (mutation.isPending guards rapid double-clicks within a
  // single in-flight request.)
  const hasStartedRef = useRef(false);
  const confirmInFlightRef = useRef(false);
  const resendInFlightRef = useRef(false);
  const folderRefreshTransitionRef = useRef<string | null>(null);

  // A different requirement starts with fresh consents and NIP guards.
  useEffect(() => {
    setAcceptTyc(false);
    setAcceptElectronicMedia(false);
    hasStartedRef.current = false;
    confirmInFlightRef.current = false;
    resendInFlightRef.current = false;
  }, [requirementId]);

  // ---------------------------------------------------------------------------
  // Phase derivation
  // ---------------------------------------------------------------------------
  const phase: Phase = useMemo(() => {
    if (!nativeBdcSupported) {
      if (reqStatus === Status.Valid) return "completed";
      if (reqStatus === Status.PendingReview) return "in_review";
      if (reqStatus === Status.Invalid) return "invalid";
      return "read_only";
    }
    if (isParticipantMismatch) return "participant_read_only";
    if (isBdcReportReplacementPending(bdcRequirement, status)) return "loading";
    if (status?.is_active === false) return "invalid";
    if (
      status?.status === "recovering" ||
      (status?.status === "capturing_inputs" &&
        status.start_recovery_pending === true)
    )
      return "recovering";
    if (
      status?.requirement_status === Status.Invalid &&
      !isLegacyBdcCaptureRecoveryEligible(bdcRequirement, status)
    )
      return "invalid";

    if (isReadOnlyContext) {
      if (statusQuery.isError) return "error";
      if (statusQuery.isLoading && !status) return "loading";
      if (status) {
        if (status.status === "completed") {
          return "completed";
        }
        if (status.status === "invalid") {
          return "invalid";
        }
        if (status.status === "in_review") return "in_review";
        if (status.status === "long_report") return "long_report";
        if (status.status === "pin_validation") return "read_only_pin";
      }
      if (reqStatus === Status.Valid) return "completed";
      if (reqStatus === Status.PendingReview) return "in_review";
      if (reqStatus === Status.Invalid) return "invalid";
      return "read_only";
    }

    if (statusQuery.isError) {
      const error = statusQuery.error as AxiosError;
      const httpStatus = error.response?.status;
      const { code } = mapBdcAxiosError(error);
      const isTransientReadError =
        (httpStatus === undefined || httpStatus >= 500) &&
        !isDeterministicBdcError(code) &&
        !code?.startsWith("BDC") &&
        (httpStatus !== undefined ||
          code === "3997" ||
          ["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"].includes(
            error.code ?? "",
          ));
      if (isTransientReadError) {
        if (reqStatus === Status.Valid) return "completed";
        if (reqStatus === Status.PendingReview) return "in_review";
      }
      return "error";
    }

    if (!status) {
      if (reqStatus === Status.Valid) return "completed";
      if (reqStatus === Status.PendingReview) return "in_review";
      return statusQuery.isLoading ? "loading" : "error";
    }

    if (status.status === "completed") {
      return "completed";
    }
    if (status.status === "capturing_inputs") {
      if (reqStatus === Status.Valid) return "completed";
      if (reqStatus === Status.PendingReview) return "in_review";
    }
    const currentRequirementStatus = status.requirement_status ?? reqStatus;
    if (currentRequirementStatus === Status.Valid) return "completed";
    // Explicit SIC terminal state wins over a locally persisted review status.
    // Keep the capturing_inputs fallback above for stale reads during transitions.
    if (status.status === "invalid") {
      return "invalid";
    }
    if (currentRequirementStatus === Status.PendingReview) return "in_review";
    if (status.status === "in_review") return "in_review";
    if (status.status === "long_report") return "long_report";
    if (status.status === "pin_validation")
      return status.sic_source === "circulo_de_credito" ? "legacy" : "pin";
    if (status.status === "capturing_inputs")
      return status.sic_source === "circulo_de_credito" ? "legacy" : "intro";
    return "error";
  }, [
    isParticipantMismatch,
    nativeBdcSupported,
    isReadOnlyContext,
    reqStatus,
    bdcRequirement,
    status,
    statusQuery.error,
    statusQuery.isError,
    statusQuery.isLoading,
  ]);

  // Acceptance belongs to this attempt. Clear it once the server advances.
  // A request that demonstrably was not sent stays in capturing_inputs.
  useEffect(() => {
    if (status?.status && status.status !== "capturing_inputs") {
      setAcceptTyc(false);
      setAcceptElectronicMedia(false);
    }
  }, [status?.status]);

  // If a retry or a fresh server read returns to capturing_inputs, the next
  // explicit click is a valid new start. This is deliberately keyed to the
  // server state rather than a render so it cannot re-fire while pin or
  // report-processing states are active.
  useEffect(() => {
    if (status?.status === "capturing_inputs") {
      hasStartedRef.current = false;
    }
  }, [status?.status]);

  // Refresh a lagging folder after completion or a report replacement. Keep
  // the terminal fallback until the folder confirms the new mutable report.
  useEffect(() => {
    if (!nativeBdcSupported) return;
    if (statusQuery.isFetching || statusQuery.isError) return;
    const completionNeedsRefresh =
      phase === "completed" && reqStatus !== Status.Valid;
    const replacementNeedsRefresh = isBdcReportReplacementPending(
      bdcRequirement,
      status,
    );
    if (!completionNeedsRefresh && !replacementNeedsRefresh) {
      folderRefreshTransitionRef.current = null;
      return;
    }
    const transition = `${folder.id}:${requirementId}:${reqStatus}`;
    if (folderRefreshTransitionRef.current === transition) return;
    folderRefreshTransitionRef.current = transition;
    void invalidateFolder();
  }, [
    folder.id,
    nativeBdcSupported,
    requirementId,
    phase,
    reqStatus,
    statusQuery.isFetching,
    statusQuery.isError,
    status?.status,
    status,
    bdcRequirement,
    invalidateFolder,
  ]);

  // ---------------------------------------------------------------------------
  // Derived NIP counters (status cache is updated by the mutations).
  // ---------------------------------------------------------------------------
  const retryPin = status?.retry_pin ?? DEFAULT_RETRY_PIN;
  const resendPin = status?.resend_pin ?? DEFAULT_RESEND_PIN;
  const maskedEmail = status?.masked_email;

  const consentsGiven = acceptTyc && acceptElectronicMedia;
  const startPending = startMutation.isPending;
  const bdcProcessLocksNavigation =
    usesNativeBdc &&
    !isReadOnlyContext &&
    (startPending ||
      confirmMutation.isPending ||
      resendMutation.isPending ||
      phase === "pin");

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------
  const resyncStatus = useCallback(() => {
    if (requirementId && nativeBdcSupported) void refetchStatus();
  }, [nativeBdcSupported, refetchStatus, requirementId]);

  const statusError = useMemo(() => {
    if (!statusQuery.isError) {
      return null;
    }
    return mapBdcAxiosError(statusQuery.error);
  }, [statusQuery.error, statusQuery.isError]);
  const statusErrorMessage = !requirementId
    ? "No encontramos la autorización en el paso seleccionado. Vuelve a seleccionar el paso del participante."
    : (statusError?.message ??
      "Ocurrio un problema temporal al consultar el estado de tu " +
        "autorizacion. Vuelve a intentarlo en unos momentos.");
  const canRetryStatusError =
    Boolean(requirementId) && !isDeterministicBdcError(statusError?.code);

  const retryStatusError = useCallback(() => {
    if (!canRetryStatusError) return;
    resyncStatus();
  }, [canRetryStatusError, resyncStatus]);

  const showRecoverableFieldError = useCallback(
    (
      message: string,
      fields: BdcRecoverableField[],
      fieldErrors: Record<string, string>,
    ) => {
      const dataValidationBlocker = fields.some(
        (field) => field.target_slug === "data-validation",
      );
      const currentSubfolder = resolveRequirementSubfolder(
        folder,
        bdcRequirement,
      );
      const identityValidationBlocker = Boolean(
        currentSubfolder &&
          (hasValidatedPersonForm(currentSubfolder.requirements) ||
            currentSubfolder.requirements.some(
              (requirement) =>
                isIdentityRequirement(requirement) &&
                (requirement.status === Status.Valid ||
                  requirement.workflow_step === "completed"),
            )) &&
          fields.some(
            (field) =>
              isIdentityRequirement({
                slug: field.target_slug,
                type: field.target_slug,
              }) ||
              currentSubfolder.requirements.some(
                (requirement) =>
                  isIdentityRequirement(requirement) &&
                  (requirement.slug === field.target_slug ||
                    requirement.type === field.target_slug),
              ),
          ),
      );
      if (
        (dataValidationBlocker || identityValidationBlocker) &&
        onNavigateToDataValidation
      ) {
        const description = combineFieldErrorDescriptions(
          buildRecoverableFieldDescription(fields, true),
          fieldErrors,
        );
        toast.error(message, {
          ...(description ? { description } : {}),
          action: {
            label: "Corregir datos",
            onClick: () => {
              if (
                !onNavigateToDataValidation({
                  requiresIdentityReview:
                    identityValidationBlocker ||
                    requiresIdentityReviewForBdcFields(fields),
                })
              ) {
                toast.error(
                  "No es posible corregir estos datos desde este paso. Contacta a quien te compartió el expediente para solicitar una revisión.",
                );
              }
            },
          },
        });
        return;
      }

      if (identityValidationBlocker) {
        toast.error(message, {
          description: combineFieldErrorDescriptions(
            buildRecoverableFieldDescription(fields, false),
            fieldErrors,
          ),
        });
        return;
      }

      const destination = resolveRequirementTarget(
        folder,
        fields,
        bdcRequirement,
      );
      // The BFF names the owner of company fields. Respect that form's
      // existing actor edit policy instead of reopening a reviewed form.
      if (
        destination?.requirement.slug === "business-form" &&
        destination.requirement.status !== Status.PendingUpload &&
        destination.requirement.status !== Status.Invalid
      ) {
        if (destination.requirement.status === Status.PendingReview) {
          const description = combineFieldErrorDescriptions(
            "El formulario de la empresa está en revisión. Ábrelo y selecciona «Deshacer el envío a revisión» para corregir los datos y volver a enviarlo.",
            fieldErrors,
          );
          toast.error(message, {
            description,
            action: {
              label: "Corregir datos",
              onClick: () => {
                if (onNavigateToRequirement) {
                  onNavigateToRequirement(
                    destination.requirement,
                    destination.subfolderId,
                  );
                  return;
                }
                dispatch(setActiveVaultTab(destination.subfolderId));
                dispatch(setActiveRequirement(destination.requirement));
              },
            },
          });
          return;
        }
        toast.error(message, {
          description: combineFieldErrorDescriptions(
            "El formulario de la empresa está aprobado y no se puede cambiar desde aquí. Solicita al personal autorizado que lo corrija desde la vista interna del expediente.",
            fieldErrors,
          ),
        });
        return;
      }
      const description = combineFieldErrorDescriptions(
        buildRecoverableFieldDescription(fields, destination !== null),
        fieldErrors,
      );

      toast.error(message, {
        ...(description ? { description } : {}),
        ...(destination
          ? {
              action: {
                label: "Corregir datos",
                onClick: () => {
                  if (onNavigateToRequirement) {
                    onNavigateToRequirement(
                      destination.requirement,
                      destination.subfolderId,
                      isBdcCompanyDataRequirement(
                        destination.requirement.slug,
                        destination.requirement.type,
                      )
                        ? { forceBdcCompanyDataEdit: true }
                        : undefined,
                    );
                    return;
                  }
                  dispatch(setActiveVaultTab(destination.subfolderId));
                  dispatch(setActiveRequirement(destination.requirement));
                },
              },
            }
          : {}),
      });
    },
    [
      bdcRequirement,
      dispatch,
      folder,
      onNavigateToDataValidation,
      onNavigateToRequirement,
    ],
  );

  const handleMutationError = useCallback(
    (error: unknown) => {
      const {
        code,
        message,
        recoverable,
        fields = [],
        fieldErrors = {},
      } = mapBdcAxiosError(error);
      if (recoverable && fields.length > 0) {
        showRecoverableFieldError(message, fields, fieldErrors);
        resyncStatus();
        return;
      }

      const fieldErrorDescription = combineFieldErrorDescriptions(
        undefined,
        fieldErrors,
      );
      if (fieldErrorDescription) {
        toast.error(message, { description: fieldErrorDescription });
      } else {
        toast.error(message);
      }
      // Terminal upstream states are reflected by re-reading bdc/status.
      if (code && ALREADY_COMPLETED_CODES.has(code)) {
        void invalidateFolder();
      }
      resyncStatus();
    },
    [invalidateFolder, resyncStatus, showRecoverableFieldError],
  );

  /**
   * Keep terminal and asynchronous outcomes truthful in user-facing feedback.
   * In particular, `in_review` is not completion: the requirement remains
   * pending review until the operator/BFF marks it terminal.
   */
  const notifyBdcState = useCallback(
    (nextStatus: string, source: "start" | "confirm") => {
      if (nextStatus === "completed") {
        toast.success(
          source === "confirm"
            ? "NIP confirmado. Autorización completada."
            : "Consulta de Buró completada.",
        );
        return;
      }
      if (nextStatus === "in_review") {
        toast.info(
          source === "confirm"
            ? "NIP confirmado. La autorización quedó en revisión."
            : "La autorización quedó en revisión. Te avisaremos cuando termine.",
        );
        return;
      }
      if (nextStatus === "long_report") {
        toast.info(
          source === "confirm"
            ? "NIP confirmado. Estamos preparando un reporte extenso."
            : "La consulta sigue en proceso; puedes actualizar su estado más tarde.",
        );
        return;
      }
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // START (fires the NIP) — only from an explicit user click.
  // ---------------------------------------------------------------------------
  const fireStart = useCallback(() => {
    // Anti double-fire: `startPending` (mutation.isPending) only flips on the
    // next render, so a rapid double-click could slip a second request through
    // before React re-renders. The synchronous guard blocks that second click.
    if (
      !usesNativeBdc ||
      !requirementId ||
      startPending ||
      hasStartedRef.current
    )
      return;
    hasStartedRef.current = true;

    const body: BdcStartRequest = {
      accept_tyc: acceptTyc,
      accept_electronic_media: acceptElectronicMedia,
    };

    startMutation.mutate(
      { reqId: requirementId, body },
      {
        onSuccess: (data) => {
          // The phase recomputes from the freshly cached status (pin /
          // completed / etc.). The NIP has been fired, so we KEEP
          // `hasStartedRef` set to prevent a re-dispatch.
          if (data.status === "capturing_inputs") {
            // This server state permits another explicit attempt.
            hasStartedRef.current = false;
          }
          if (
            data.status !== "pin_validation" &&
            data.status !== "capturing_inputs"
          ) {
            notifyBdcState(data.status, "start");
          }
        },
        onError: (error) => {
          // Re-read the server state before offering another attempt.
          hasStartedRef.current = false;
          const { code, message } = mapBdcAxiosError(error);
          if (code === CONSENT_MISSING_CODE) {
            // Server-side consent gate (defense in depth: the UI already blocks
            // "Continuar" without both consents). Bounce the user back to the
            // consent intro with a clear toast.
            toast.error(message);
            return;
          }
          handleMutationError(error);
        },
      },
    );
  }, [
    requirementId,
    usesNativeBdc,
    startPending,
    acceptTyc,
    acceptElectronicMedia,
    startMutation,
    handleMutationError,
    notifyBdcState,
  ]);

  const handleContinue = useCallback(() => {
    if (!consentsGiven || startPending) return;
    fireStart();
  }, [consentsGiven, fireStart, startPending]);

  // ---------------------------------------------------------------------------
  // CONFIRM / RESEND
  // ---------------------------------------------------------------------------
  const handleConfirmPin = useCallback(
    (pin: string, confirmPin: string) => {
      if (
        !usesNativeBdc ||
        !requirementId ||
        confirmMutation.isPending ||
        confirmInFlightRef.current
      )
        return;
      confirmInFlightRef.current = true;
      confirmMutation.mutate(
        { reqId: requirementId, pin, confirmPin },
        {
          onSuccess: (data) => {
            confirmInFlightRef.current = false;
            notifyBdcState(data.status, "confirm");
          },
          onError: (error) => {
            confirmInFlightRef.current = false;
            const { code } = mapBdcAxiosError(error);
            if (code === WRONG_PIN_CODE) {
              setPinEntryResetKey((current) => current + 1);
            }
            handleMutationError(error);
          },
        },
      );
    },
    [
      usesNativeBdc,
      requirementId,
      confirmMutation,
      handleMutationError,
      notifyBdcState,
    ],
  );

  const handleResendPin = useCallback(
    (method: BdcResendMethod) => {
      if (
        !usesNativeBdc ||
        !requirementId ||
        resendMutation.isPending ||
        resendInFlightRef.current
      )
        return;
      resendInFlightRef.current = true;
      resendMutation.mutate(
        { reqId: requirementId, method },
        {
          onSuccess: (data) => {
            resendInFlightRef.current = false;
            setPinEntryResetKey((current) => current + 1);
            if (data.status === "pin_validation") {
              toast.success(
                method === "Email"
                  ? "Reenviamos tu NIP por correo electronico."
                  : "Reenviamos tu NIP por SMS.",
              );
            }
          },
          onError: (error) => {
            resendInFlightRef.current = false;
            setPinEntryResetKey((current) => current + 1);
            handleMutationError(error);
          },
        },
      );
    },
    [usesNativeBdc, requirementId, resendMutation, handleMutationError],
  );

  const footerSubmitRef = useRef<() => Promise<boolean>>(async () => false);
  footerSubmitRef.current = async () => {
    if (phase === "intro") {
      if (!consentsGiven) {
        toast.error("Acepta los consentimientos para continuar.");
        return false;
      }
      handleContinue();
      return false;
    }
    if (phase === "pin") {
      toast.info("Confirma el NIP para continuar.");
      return false;
    }
    if (phase === "error") {
      retryStatusError();
      return false;
    }
    return false;
  };

  useEffect(() => {
    if (!onRegisterSubmit) return;
    // Terminal/read-only and processing states have their own card action (or
    // no action). Do not register a fake global submit, which would make a
    // staff modal show "Enviar a revisión" or leave a dead footer action.
    const canRegisterSubmit =
      Boolean(requirementId) &&
      !isReadOnlyContext &&
      ["intro", "pin", "error"].includes(phase);
    if (!canRegisterSubmit) {
      onRegisterSubmit(null);
      return;
    }
    const submit = () => footerSubmitRef.current();
    onRegisterSubmit(submit);
    return () => onRegisterSubmit(null);
  }, [isReadOnlyContext, onRegisterSubmit, phase, requirementId]);

  useEffect(() => {
    if (!onSetNavigationHidden) return;
    onSetNavigationHidden(bdcProcessLocksNavigation);
    return () => onSetNavigationHidden(false);
  }, [bdcProcessLocksNavigation, onSetNavigationHidden]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (phase === "loading") {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-[#28397a]" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Cargando autorizacion...
        </h3>
        <p className="text-sm text-slate-500">Esto tomara solo un momento.</p>
      </div>
    );
  }

  if (phase === "legacy") {
    const params = new URLSearchParams({
      return_url: `${window.location.origin}/vault/${folder.id}`,
    });
    if (bdcSubfolder?.slug.includes("guarantor"))
      params.set("is_guarantor", "true");
    const authorizationUrl = API_MB_PLATFORM_HOST
      ? `${API_MB_PLATFORM_HOST.replace(/\/$/, "")}/tenants/credit-history/folder/${encodeURIComponent(folder.id)}?${params}`
      : null;
    return (
      <div className="flex flex-col items-center px-4 py-16 text-center">
        <h3 className="mb-2 text-xl font-semibold text-slate-900">
          Autorización de Círculo de Crédito
        </h3>
        <p className="mb-6 max-w-md text-sm leading-relaxed text-slate-500">
          Continúa la autorización en la plataforma de consulta. Al terminar,
          regresa a este expediente para consultar su estado.
        </p>
        {authorizationUrl ? (
          <Button asChild>
            <a
              href={authorizationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Continuar autorización
            </a>
          </Button>
        ) : (
          <p role="alert" className="text-sm text-red-600">
            No pudimos abrir la plataforma de consulta. Contacta a soporte.
          </p>
        )}
      </div>
    );
  }

  if (phase === "error") {
    return (
      <NativeShell>
        <div
          role="alert"
          className="py-10 px-4 sm:px-6 flex flex-col items-center text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            No pudimos cargar la autorizacion
          </h3>
          <p className="text-sm text-slate-500 max-w-md mb-4">
            {statusErrorMessage}
          </p>
          {canRetryStatusError && (
            <Button
              variant="outline"
              onClick={retryStatusError}
              disabled={statusQuery.isFetching}
              className="border-slate-200 hover:bg-slate-50"
            >
              {statusQuery.isFetching ? "Reintentando..." : "Reintentar"}
            </Button>
          )}
        </div>
      </NativeShell>
    );
  }

  // Terminal states render as bare centered state screens (same treatment as
  // DigitalSignature's completed state — no step header, no process card).
  if (phase === "completed") {
    const recordStatus = status?.requirement_status ?? reqStatus;
    if (reqStatus !== Status.Valid || recordStatus !== Status.Valid) {
      const isRejected =
        reqStatus === Status.Invalid || recordStatus === Status.Invalid;
      const isReview =
        reqStatus === Status.PendingReview ||
        recordStatus === Status.PendingReview;
      return (
        <BdcStatusCards
          variant={
            isRejected ? "invalid" : isReview ? "in_review" : "read_only"
          }
          title="La consulta de Buró de Crédito terminó"
          description={
            isRejected
              ? "El resultado no fue aprobado en tu expediente. Contacta a quien te compartió el expediente para revisarlo."
              : isReview
                ? "El resultado está pendiente de revisión interna en tu expediente."
                : "El registro del resultado en tu expediente está pendiente. Actualiza el estado para consultar el resultado registrado."
          }
        >
          <Button
            variant="outline"
            disabled={statusQuery.isFetching}
            onClick={() => {
              void refetchStatus();
              void invalidateFolder();
            }}
          >
            Actualizar estado y expediente
          </Button>
        </BdcStatusCards>
      );
    }
    return <BdcStatusCards variant="completed" />;
  }

  if (phase === "in_review") {
    return <BdcStatusCards variant="in_review" />;
  }

  if (phase === "long_report") {
    return (
      <NativeShell>
        <BdcStatusCards variant="long_report">
          {!isReadOnlyContext && (
            <Button
              type="button"
              variant="outline"
              onClick={resyncStatus}
              disabled={statusQuery.isFetching}
              className="border-slate-200 hover:bg-slate-50"
            >
              Actualizar estado
            </Button>
          )}
        </BdcStatusCards>
      </NativeShell>
    );
  }

  if (phase === "recovering") {
    return (
      <NativeShell>
        <BdcStatusCards
          variant="long_report"
          title="Consulta en recuperación"
          description="Estamos verificando el resultado de esta solicitud con Buró. Actualiza el estado para consultar el mismo reporte. No vuelvas a enviar el NIP ni iniciar otra consulta mientras se recupera."
        >
          <p className="mb-3 text-sm text-slate-600">
            Si el estado no cambia después de actualizarlo, contacta a soporte
            para revisar esta solicitud.
          </p>
          {statusQuery.isError && (
            <p role="alert" className="mb-3 text-sm text-slate-600">
              No pudimos actualizar el estado. Puedes volver a intentarlo; no
              inicies otra consulta.
            </p>
          )}
          {!isReadOnlyContext && (
            <Button
              type="button"
              variant="outline"
              onClick={resyncStatus}
              disabled={statusQuery.isFetching}
              className="border-slate-200 hover:bg-slate-50"
            >
              Actualizar estado
            </Button>
          )}
        </BdcStatusCards>
      </NativeShell>
    );
  }

  if (phase === "read_only") {
    if (!nativeBdcSupported) {
      return (
        <BdcStatusCards
          variant="read_only"
          title="Autorización digital no disponible"
          description="La autorización digital no está disponible para este participante. Contacta a quien te compartió el expediente para revisar este requisito."
        />
      );
    }
    return <BdcStatusCards variant="read_only" />;
  }

  if (phase === "read_only_pin") {
    return (
      <BdcStatusCards
        variant="read_only"
        title="NIP pendiente de confirmación"
        description="El NIP ya fue enviado al participante. Esta vista es de solo lectura; la confirmación debe realizarse desde el expediente del participante."
      />
    );
  }

  if (phase === "participant_read_only") {
    const participantLabel =
      expectedActor === "guarantor" ? "fiador" : "inquilino";
    return (
      <BdcStatusCards
        variant="read_only"
        title={`Autorización del ${participantLabel}`}
        description={
          expectedActor === "guarantor"
            ? "Este paso puede completarlo el fiador, el inquilino o el asesor desde su acceso al expediente. El NIP se envía al contacto del fiador."
            : "Este paso puede completarlo el inquilino o el asesor desde su acceso al expediente. El NIP se envía al contacto del inquilino."
        }
      />
    );
  }

  if (phase === "invalid") {
    return <BdcStatusCards variant="invalid" />;
  }

  if (phase === "pin") {
    return (
      <NativeShell>
        <BdcPinConfirm
          key={`${requirementId ?? "bdc"}-${pinEntryResetKey}`}
          maskedEmail={maskedEmail}
          retryPin={retryPin}
          resendPin={resendPin}
          isLoading={confirmMutation.isPending || resendMutation.isPending}
          onConfirm={handleConfirmPin}
          onResend={handleResendPin}
        />
      </NativeShell>
    );
  }

  // phase === "intro"
  return (
    <NativeShell showProcess>
      <div className="space-y-6">
        <BdcConsents
          acceptTyc={acceptTyc}
          acceptElectronicMedia={acceptElectronicMedia}
          onAcceptTycChange={setAcceptTyc}
          onAcceptElectronicMediaChange={setAcceptElectronicMedia}
          disabled={startPending}
        />

        <div className="flex flex-col items-center gap-3 pt-2">
          <Button
            disabled={!consentsGiven || startPending}
            onClick={handleContinue}
            className="h-12 w-full max-w-sm gap-2 text-base bg-[#28397a] hover:bg-[#1e2d5f]"
          >
            {startPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              "Continuar"
            )}
          </Button>
          <p className="flex items-center gap-1.5 text-xs text-slate-400">
            <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
            Conexión segura. Tus datos solo se usan para esta consulta.
          </p>
        </div>
      </div>
    </NativeShell>
  );
};

/** Los 3 pasos reales del flujo — el orden SÍ es información. */
const PROCESS_STEPS = [
  {
    title: "Acepta los consentimientos",
    detail: "Lee y autoriza la consulta de tu historial crediticio.",
  },
  {
    title: "Recibe tu NIP",
    detail: "Te enviamos un código de 4 dígitos por correo electrónico.",
  },
  {
    title: "Confírmalo aquí",
    detail: "Ingresa el NIP sin salir de este expediente.",
  },
];

const NativeShell: React.FC<{
  children: React.ReactNode;
  /** Muestra la tira de pasos del proceso (solo en el intro). */
  showProcess?: boolean;
}> = ({ children, showProcess = false }) => (
  <div className="w-full">
    <div className="mb-8">
      <p className="text-gray-600">
        Autoriza la consulta a{" "}
        <span className="font-semibold text-gray-900">Buró de Crédito</span> de
        forma digital y segura.
      </p>
    </div>
    <div className="mx-auto max-w-2xl space-y-6">
      {showProcess && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-slate-700">
              Proceso 100% digital
            </h3>
          </div>
          <ol className="grid gap-4 sm:grid-cols-3">
            {PROCESS_STEPS.map((step, index) => (
              <li key={step.title} className="flex items-start gap-3">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-semibold text-[#28397a]">
                  {index + 1}
                </span>
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {step.title}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                    {step.detail}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {children}
    </div>
  </div>
);

function resolveBdcRequirement(
  folder: Folder,
  activeTab: string | null,
  activeRequirement: Requirement | null,
  scopedRequirementId: string | undefined,
): Requirement | undefined {
  const selectedId = scopedRequirementId ?? activeRequirement?.id;
  const selectedRequirement = folder.subfolders
    ?.flatMap((subfolder) => subfolder.requirements)
    .find(
      (requirement) =>
        requirement.id === selectedId &&
        isBdcAuthorizationRequirement(requirement.slug, requirement.type),
    );
  // Vault renders its selected step before Redux synchronizes. That explicit
  // scope is authoritative, including when the requirement no longer exists.
  if (scopedRequirementId !== undefined || selectedRequirement) {
    return selectedRequirement;
  }

  return folder.subfolders
    ?.find((subfolder) => subfolder.id === activeTab)
    ?.requirements.find((requirement) =>
      isBdcAuthorizationRequirement(requirement.slug, requirement.type),
    );
}

function resolveRequirementTarget(
  folder: Folder,
  fields: BdcRecoverableField[],
  bdcRequirement: Requirement | undefined,
): { requirement: Requirement; subfolderId: string } | null {
  const targetSlugs = new Set(fields.map((field) => field.target_slug));
  const currentSubfolder = resolveRequirementSubfolder(folder, bdcRequirement);
  const currentRequirement = currentSubfolder
    ? findTargetRequirement(currentSubfolder, targetSlugs)
    : undefined;

  if (currentSubfolder) {
    const requirement =
      currentRequirement ??
      buildVirtualCompanyDataTarget(currentSubfolder, targetSlugs);
    if (!requirement) return null;

    return {
      requirement,
      subfolderId: currentSubfolder.id,
    };
  }

  return null;
}

type BdcSubfolder = NonNullable<Folder["subfolders"]>[number];

function resolveRequirementSubfolder(
  folder: Folder,
  requirement: Requirement | undefined,
): BdcSubfolder | undefined {
  if (!requirement) return undefined;

  return folder.subfolders?.find((subfolder) =>
    subfolder.requirements.some((candidate) => candidate.id === requirement.id),
  );
}

function findTargetRequirement(
  subfolder: BdcSubfolder,
  targetSlugs: Set<string>,
): Requirement | undefined {
  const targetsProofOfAddress = [...targetSlugs].some((slug) =>
    isProofOfAddressRequirement({ slug, type: slug }),
  );
  return subfolder.requirements.find(
    (candidate) =>
      targetSlugs.has(candidate.slug) ||
      targetSlugs.has(candidate.type) ||
      (targetsProofOfAddress && isProofOfAddressRequirement(candidate)),
  );
}

function buildVirtualCompanyDataTarget(
  subfolder: BdcSubfolder,
  targetSlugs: Set<string>,
): Requirement | undefined {
  if (!targetSlugs.has(BDC_COMPANY_DATA_SLUG)) return undefined;
  if (!subfolder.slug.endsWith("-pm")) return undefined;
  const bdcRequirement = subfolder.requirements.find((candidate) =>
    isBdcAuthorizationRequirement(candidate.slug, candidate.type),
  );
  if (!bdcRequirement) return undefined;
  const companyData = bdcRequirement.verification_data?.bdc?.company_data;
  return createBdcCompanyDataRequirement(
    bdcRequirement,
    companyData?.reviewed === true ? Status.Valid : Status.PendingUpload,
  );
}

function buildRecoverableFieldDescription(
  fields: BdcRecoverableField[],
  hasCorrectionTarget = true,
): string | undefined {
  const labels = [...new Set(fields.map((field) => field.label))];
  const fieldDescription =
    labels.length > 0 ? `Corrige: ${labels.join(", ")}.` : undefined;

  if (hasCorrectionTarget) return fieldDescription;
  if (!fieldDescription) return MISSING_RECOVERABLE_TARGET_DESCRIPTION;
  return `${fieldDescription} ${MISSING_RECOVERABLE_TARGET_DESCRIPTION}`;
}

function combineFieldErrorDescriptions(
  description: string | undefined,
  fieldErrors: Record<string, string>,
): string | undefined {
  const details = Object.values(fieldErrors).filter(Boolean);
  const parts = [...new Set([description, ...details].filter(Boolean))];
  return parts.length > 0 ? parts.join(" ") : undefined;
}

export default BdcAuthorizationNative;
